<script lang="ts">
  import { SKILL_WINDOWS, type SkillScore } from '../../api/skill'
  import { colorFor } from '../../chart/palette'
  import { shortLabel } from '../../compare/table'
  import { compare } from '../../state/compare.svelte'
  import { layout } from '../../state/layout.svelte'
  import { location } from '../../state/location.svelte'
  import { ensureSkill, setSkillField, setSkillWindow, skill } from '../../state/skill.svelte'
  import Panel from '../Panel.svelte'

  /**
   * How the models have actually been doing here, lately.
   *
   * Model down, lead bucket across, each cell `bias / MAE` with *n*. Four
   * things on screen are load-bearing and none of them are the numbers:
   *
   * * **The station and its distance.** In open country the nearest METAR site
   *   is routinely forty miles away, and a table headed `KVEL` without that
   *   number is a claim about somewhere else's weather.
   * * **The sign convention, stated.** `+1.9` meaning "the model forecast
   *   warmer than observed" is a convention rather than a fact, and a panel
   *   that does not say which way it points is a panel half its readers read
   *   backwards.
   * * **`collecting — 3 h of 24`.** A feature that is warming up and a feature
   *   that is broken must not look the same.
   * * **What is not scored, and why.** `apcp1h` is archived and never scored,
   *   because a station reports `precipitationLastHour` as null rather than
   *   zero even on a dry night. An absent row says nothing; a sentence does.
   *
   * It reports and it stops. No ranking, no "best model", no blend weight —
   * that would be a forecast product, and this app compares models rather than
   * issuing them. The wording below makes the smaller claim on purpose: not
   * "which model is better", but "which of these has been closest *here*".
   */

  let point = $derived(location.selected)
  let body = $derived(skill.body)

  /**
   * Whether the grid can be seen. Collapsed, the panel asks for nothing: the
   * request reads one station's slab out of every archived run and a day of
   * that station's observations, for a panel nobody has opened.
   */
  let open = $state(false)

  $effect(() => {
    const here = point
    const hours = skill.hours
    if (open && here) void ensureSkill(here.lat, here.lon, hours)
  })

  /**
   * Which field's grid is shown.
   *
   * Follows the meteogram's chip where that field can be scored, so the two
   * panels agree about what is being looked at. Where it cannot — the chart
   * draws reflectivity happily and no station measures it — this falls back to
   * its own first field rather than showing an empty grid.
   */
  let fields = $derived(body?.fields ?? [])
  let field = $derived.by(() => {
    const ids = fields.map((f) => f.id)
    if (skill.field && ids.includes(skill.field)) return skill.field
    if (ids.includes(compare.field)) return compare.field
    return ids[0] ?? null
  })
  let fieldInfo = $derived(fields.find((f) => f.id === field) ?? null)

  /** Models with at least one archived run, plus the ones that say why not. */
  let rows = $derived(body?.models ?? [])
  let scored = $derived(rows.filter((m) => !m.reason))
  let missing = $derived(rows.filter((m) => m.reason))

  let byCell = $derived.by(() => {
    const out = new Map<string, SkillScore>()
    for (const s of body?.scores ?? []) out.set(`${s.model}|${s.field}|${s.bucket}`, s)
    return out
  })

  function cellFor(model: string, bucket: string): SkillScore | undefined {
    return field ? byCell.get(`${model}|${field}|${bucket}`) : undefined
  }

  // One decimal at least: a bias of 0 and a bias of 0.4 are different claims,
  // and `decimals` is 0 for temperature because a *forecast* of 71.3 °F is
  // false precision where a mean error of 1.6 °F is not.
  let places = $derived(Math.max(1, fieldInfo?.decimals ?? 0))

  /** A temperature error carries a degree sign, as round 2 draws it. */
  let unit = $derived(fieldInfo?.units.startsWith('°') ? '°' : '')

  /** The bias, signed — the sign is the whole information in it. A minus is
   *  the typographic one, the width of the plus beside it. */
  function fmtBias(value: number | null): string {
    if (value === null || !fieldInfo) return '—'
    const sign = value > 0 ? '+' : value < 0 ? '−' : ''
    return `${sign}${Math.abs(value).toFixed(places)}${unit}`
  }

  /** The MAE, unsigned. A mean *absolute* error cannot be negative, so a `+`
   *  on it is noise — and beside a signed bias it reads as a second sign. */
  function fmtMae(value: number | null): string {
    if (value === null || !fieldInfo) return '—'
    return `${value.toFixed(places)}${unit}`
  }

  let windowLabel = $derived.by(() => {
    const hours = body?.window.hours ?? 0
    return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} days`
  })

  /** `collecting — 3 h of 24`, or null once the archive spans the window. */
  let collecting = $derived.by(() => {
    const w = body?.window
    if (!w) return null
    // A whole hour of slack: the newest run's last step lands a few minutes
    // short of the window's edge on every pass, and "23.8 h of 24" forever
    // would read as a permanent fault.
    if (w.coverage_hours >= w.hours - 1) return null
    return `collecting — ${w.coverage_hours} h of ${w.hours}`
  })

  let observedError = $derived(
    body?.observed_errors.find((e) => e.field === field)?.reason ?? null,
  )
</script>

{#if !skill.absent}
  <Panel id="skill" title="Model skill" bind:open>
    {#if !point}
      <p class="muted">Tap the map or search to see how the models have been doing.</p>
    {:else if skill.status === 'loading' && !body}
      <p class="muted loading">Reading the archive…</p>
    {:else if body && !body.station}
      <!-- A point nowhere near a station we sampled. Not an error: it is the
           right answer for the middle of the Pacific, and it is different from
           an archive with nothing in it. -->
      <p class="muted">No verification station near this point.</p>
    {:else if body}
      <div class="body">
        {#if fields.length > 1}
          <div class="wx-chips" class:scroll={!layout.isDesktop} role="tablist" aria-label="Skill field">
            {#each fields as option (option.id)}
              <button
                type="button"
                role="tab"
                class="wx-chip touch-target"
                class:on={option.id === field}
                aria-selected={option.id === field}
                onclick={() => setSkillField(option.id)}
              >
                {option.label}
              </button>
            {/each}
          </div>
        {/if}

        <!-- Provenance first, because every number below is *about* this
             station rather than about the point that was tapped. The sign
             convention is on the second line, stated rather than inferred. -->
        <p class="provenance">
          <strong
            >Against {body.station?.id}{body.station?.name ? ` (${body.station.name})` : ''}, {body
              .station?.distance_mi} mi</strong
          >
          <span class="muted">
            Last {windowLabel} · {body.window.observations} obs · bias = forecast − observed ·
            MAE = mean absolute error{fieldInfo ? ` · ${fieldInfo.units}` : ''}
          </span>
        </p>

        <div class="setting" role="group" aria-label="History window">
          {#if layout.isDesktop}<span class="setting-label" aria-hidden="true">History</span>{/if}
          <span class="wx-seg">
            {#each SKILL_WINDOWS as option (option.hours)}
              <button
                type="button"
                class="touch-target"
                class:on={skill.hours === option.hours}
                aria-pressed={skill.hours === option.hours}
                onclick={() => setSkillWindow(option.hours)}
              >
                {option.label}
              </button>
            {/each}
          </span>
        </div>

        {#if collecting}
          <p class="collecting">{collecting}</p>
        {/if}

        {#if observedError}
          <!-- The station's observations failed, not the models. Saying "the
               models have no skill here" because a fetch 404'd would be the
               worst thing this panel could do. -->
          <p class="muted note">{observedError}</p>
        {:else if scored.length === 0 || body.buckets.length === 0}
          <p class="muted">Nothing archived for this point yet.</p>
        {:else if fieldInfo}
          <div class="scroll">
            <table style:--buckets={body.buckets.length}>
              <caption class="visually-hidden">
                Mean bias and mean absolute error in {fieldInfo.units} for {fieldInfo.label}, by
                model and lead time, against {body.station?.id}. A positive bias means the model
                forecast higher than the station measured. Fewer than {body.min_pairs} matched pairs
                is not scored.
              </caption>
              <thead>
                <tr>
                  <th scope="col" class="who">Lead</th>
                  {#each body.buckets as bucket (bucket.id)}
                    <th scope="col">{bucket.label}</th>
                  {/each}
                </tr>
              </thead>
              <tbody>
                {#each scored as model (model.id)}
                  <tr>
                    <th scope="row" class="who" title={model.label}>
                      <span class="key" style:background={colorFor('model', model.id)}></span>
                      <span class="label">
                        <span aria-hidden="true">{shortLabel({ id: model.id, kind: 'model', label: model.label })}</span>
                        <span class="visually-hidden">{model.label}</span>
                      </span>
                    </th>
                    {#each body.buckets as bucket (bucket.id)}
                      {@const found = cellFor(model.id, bucket.id)}
                      <td>
                        {#if found && found.bias !== null}
                          <span class="big">{fmtBias(found.bias)}</span>
                          <span class="small">MAE {fmtMae(found.mae)}</span>
                          <span class="n">n {found.n.toLocaleString('en-US')}</span>
                        {:else if found}
                          <!-- Two pairs is not a skill measurement, and a
                               number here would be read as one. The count
                               still shows, so "warming up" and "nothing at
                               all" differ. -->
                          <span class="big few">Too few</span>
                          <span class="small muted">needs {body.min_pairs}</span>
                          <span class="n">n {found.n}</span>
                        {:else}
                          <span class="big muted">–</span>
                          <span class="small muted">No pairs</span>
                        {/if}
                      </td>
                    {/each}
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}

        {#if fieldInfo && scored.length > 0 && !observedError}
          <p class="legend">
            <!-- Stated, never inferred. -->
            A positive bias means the model forecast higher than {body.station?.id} measured, in
            {fieldInfo.units}. Fewer than {body.min_pairs} matched pairs is too few to score.
          </p>
        {/if}

        {#if missing.length > 0}
          <ul class="unavailable">
            {#each missing as model (model.id)}
              <li><span class="who-id">{model.id}</span> · {model.reason}</li>
            {/each}
          </ul>
        {/if}

        {#if body.unscored.length > 0}
          <!-- Archived, never scored, and each one says why: an absent row
               and a field nobody ingested look identical otherwise. -->
          <div class="unscored">
            <div class="wx-rule"><span class="eyebrow">Not scored</span></div>
            {#each body.unscored as entry (entry.field)}
              <div class="unscored-row">
                <span class="unscored-field">{entry.label}</span>
                <span class="unscored-why">{entry.reason}</span>
              </div>
            {/each}
          </div>
        {/if}

        <p class="caveat">
          One station in a grid cell kilometres across, over one window. This says which model has
          been closest here lately — not which model is better.
        </p>
      </div>
    {/if}

    {#if skill.error}
      <p class="error">{skill.error}</p>
    {/if}
  </Panel>
{/if}

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .body p {
    margin: 0;
  }

  .provenance {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
    line-height: 1.45;
  }

  .provenance strong {
    font-weight: 800;
  }

  .setting {
    display: grid;
    grid-template-columns: 76px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
  }

  .setting:has(> .wx-seg:only-child) {
    grid-template-columns: minmax(0, 1fr);
  }

  .setting-label {
    font-size: 12px;
    color: var(--color-muted);
  }

  .collecting {
    font-size: 12px;
    font-weight: 800;
  }

  .scroll {
    overflow-x: auto;
    /* The scroll here is horizontal; a vertical drag scrolls the panel. */
    touch-action: pan-x pan-y;
  }

  table {
    width: 100%;
    min-width: 330px;
    table-layout: fixed;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;
  }

  thead th {
    padding: 0 0 5px 8px;
    border-bottom: 2px solid var(--color-text);
    font-size: 10px;
    font-weight: 800;
    text-align: left;
    white-space: nowrap;
  }

  th.who {
    width: 84px;
    padding-left: 0;
    text-align: left;
  }

  tbody tr {
    border-bottom: 1px solid var(--color-divider);
  }

  tbody th.who {
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
  }

  .key {
    display: inline-block;
    width: 14px;
    height: 3px;
    margin-right: 8px;
    vertical-align: middle;
  }

  .label {
    vertical-align: middle;
  }

  td {
    padding: 7px 0 7px 8px;
    border-left: 1px solid var(--color-divider);
    vertical-align: top;
  }

  td > span {
    display: block;
  }

  .big {
    font-size: 15px;
    font-weight: 800;
    line-height: 18px;
  }

  .big.few {
    font-size: 12px;
  }

  .small {
    font-size: 11px;
    line-height: 14px;
  }

  .n {
    font-size: 10px;
    line-height: 13px;
    color: var(--color-muted);
  }

  .unavailable {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 11px;
    line-height: 1.5;
    color: var(--color-muted);
  }

  .who-id {
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .unscored {
    display: flex;
    flex-direction: column;
    padding-top: 4px;
  }

  .unscored-row {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 7px 0;
    border-bottom: 1px solid var(--color-divider);
  }

  .unscored-field {
    font-size: 12px;
    font-weight: 800;
  }

  .unscored-why {
    font-size: 11px;
    line-height: 1.4;
    color: var(--color-muted);
  }

  .note,
  .legend,
  .caveat {
    font-size: 11px;
    line-height: 1.45;
    color: var(--color-muted);
  }
</style>
